import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

export type MegaMenuSubItem = {
  label: string;
  description?: string;
  href: string;
  icon?: React.ElementType;
};

export type MegaMenuSubMenu = {
  title: string;
  items: MegaMenuSubItem[];
};

export type MegaMenuItem = {
  id: string;
  label: string;
  href: string;
  subMenus?: MegaMenuSubMenu[];
};

export interface MegaMenuProps {
  items: MegaMenuItem[];
  isActive: (href: string) => boolean;
  onNavigate: (href: string) => void;
  isRTL?: boolean;
  className?: string;
}

export function MegaMenu({ 
  items, 
  isActive, 
  onNavigate, 
  isRTL = false,
  className 
}: MegaMenuProps) {
  const [openMenu, setOpenMenu] = React.useState<string | null>(null);
  const [hoveredItem, setHoveredItem] = React.useState<string | null>(null);

  const handleMouseEnter = (menuId: string) => {
    setOpenMenu(menuId);
    setHoveredItem(menuId);
  };

  const handleMouseLeave = () => {
    setOpenMenu(null);
    setHoveredItem(null);
  };

  return (
    <nav className={cn("flex items-center gap-1", className)}>
      {items.map((navItem) => (
        <div
          key={navItem.id}
          className="relative"
          onMouseEnter={() => handleMouseEnter(navItem.id)}
          onMouseLeave={handleMouseLeave}
        >
          {navItem.subMenus ? (
            // Menu item with dropdown
            <button
              className="relative flex items-center gap-1.5 px-4 py-2.5 font-medium text-primary-foreground/90 hover:text-primary-foreground transition-colors duration-200"
              onMouseEnter={() => setHoveredItem(navItem.id)}
              onMouseLeave={() => !openMenu && setHoveredItem(null)}
            >
              {navItem.label}
              <ChevronDown 
                className={cn(
                  "w-4 h-4 transition-transform duration-300",
                  openMenu === navItem.id && "rotate-180"
                )} 
              />
              
              {/* Animated underline */}
              {(hoveredItem === navItem.id || isActive(navItem.href)) && (
                <motion.div
                  layoutId="navbar-underline"
                  className="absolute bottom-0 left-2 right-2 h-0.5 bg-accent rounded-full"
                  initial={false}
                  transition={{ type: "spring", bounce: 0.2, duration: 0.4 }}
                />
              )}
            </button>
          ) : (
            // Simple link
            <button
              onClick={() => onNavigate(navItem.href)}
              className="relative flex items-center px-4 py-2.5 font-medium text-primary-foreground/90 hover:text-primary-foreground transition-colors duration-200"
              onMouseEnter={() => setHoveredItem(navItem.id)}
              onMouseLeave={() => setHoveredItem(null)}
            >
              {navItem.label}
              
              {/* Animated underline */}
              {(hoveredItem === navItem.id || isActive(navItem.href)) && (
                <motion.div
                  layoutId="navbar-underline"
                  className="absolute bottom-0 left-2 right-2 h-0.5 bg-accent rounded-full"
                  initial={false}
                  transition={{ type: "spring", bounce: 0.2, duration: 0.4 }}
                />
              )}
            </button>
          )}

          {/* Mega Menu Dropdown Panel */}
          <AnimatePresence>
            {openMenu === navItem.id && navItem.subMenus && (
              <motion.div
                initial={{ opacity: 0, y: 15, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 15, scale: 0.95 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className={cn(
                  "absolute top-full pt-3 z-50",
                  isRTL ? "right-0" : "left-0"
                )}
              >
                <div className="bg-card border border-border/50 rounded-2xl shadow-2xl overflow-hidden min-w-[280px]">
                  {/* Accent top border */}
                  <div className="h-1 bg-gradient-to-r from-accent via-accent/80 to-accent/60" />
                  
                  <div className="p-4">
                    <div className={cn(
                      "grid gap-6",
                      navItem.subMenus.length > 1 
                        ? `grid-cols-${Math.min(navItem.subMenus.length, 3)}` 
                        : "grid-cols-1"
                    )}
                    style={{
                      gridTemplateColumns: `repeat(${Math.min(navItem.subMenus.length, 3)}, minmax(200px, 1fr))`
                    }}
                    >
                      {navItem.subMenus.map((sub, subIndex) => (
                        <div key={subIndex} className="space-y-3">
                          {/* Group Title */}
                          <h3 className="text-xs font-semibold uppercase tracking-wider text-accent px-3">
                            {sub.title}
                          </h3>
                          
                          {/* Group Items */}
                          <div className="space-y-1">
                            {sub.items.map((item, itemIndex) => {
                              const Icon = item.icon;
                              return (
                                <button
                                  key={itemIndex}
                                  onClick={() => {
                                    onNavigate(item.href);
                                    setOpenMenu(null);
                                  }}
                                  className={cn(
                                    "w-full flex items-start gap-3 px-3 py-2.5 rounded-xl text-start",
                                    "transition-all duration-200",
                                    "hover:bg-accent/10 group"
                                  )}
                                >
                                  {Icon && (
                                    <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center group-hover:bg-accent/20 transition-colors">
                                      <Icon className="w-4 h-4 text-accent" />
                                    </div>
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <span className="block text-sm font-medium text-foreground group-hover:text-accent transition-colors">
                                      {item.label}
                                    </span>
                                    {item.description && (
                                      <span className="block text-xs text-muted-foreground mt-0.5 line-clamp-2">
                                        {item.description}
                                      </span>
                                    )}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </nav>
  );
}
